/* Calzado Audor · la navegación
   ---------------------------------------------------------------------------
   Lo que hace este archivo, y nada más:

   1 · AVISA QUE YA ESTÁ. Pone `aud-menu-ok` en <html>, y solo entonces el CSS apaga los
       submenús de Horizon. Si este archivo no carga, el menú nativo sigue funcionando
       completo: el fallo cae del lado seguro. `aud-cajon-ok` va aparte, porque el cajón
       propio se puede apagar desde el editor y el nativo tiene que volver.

   2 · MIDE LAS ANCLAS. Seis números, los seis leídos del encabezado VIVO y ninguno supuesto.
       Con el aviso encendido, con el encabezado pegado y con el aviso ya fuera de pantalla
       salen distintos. Es la lección de L-15: comprobar antes de culpar al código.

         · `--aud-m-top`   borde inferior del encabezado  → donde empieza el panel de escritorio
         · `--aud-m-aviso` borde inferior del AVISO       → donde empieza el cajón de celular
         · `--aud-m-barra` alto del encabezado            → alto de la barra del cajón
         · `--aud-m-lado`  su `--header-mobile-bookend`   → ancho de las columnas de iconos
         · `--aud-m-pad`   su relleno lateral             → el mismo en el cajón
         · `--aud-m-logo`  alto de su logo                → el mismo en el cajón

       POR QUÉ SEIS Y NO UNO. El cajón tiene que quedar CALCADO sobre el encabezado cerrado, y
       eso son dos cosas distintas:

         · DÓNDE EMPIEZA Y CUÁNTO MIDE (`aviso` y `barra`). Iba a `inset: 0` y tapaba el aviso
           entero, así que al abrirlo el logo saltaba 37 px hacia arriba; y su barra medía 56 px
           fijos contra los 66 del encabezado.
         · CÓMO REPARTE EL ANCHO (`lado`, `pad` y `logo`). Horizon monta la barra de celular en
           una rejilla de cinco columnas —`leftA leftB center rightA rightB`, las cuatro de los
           lados a 44 px y el logo en la del medio—, o sea que el logo está CENTRADO de verdad.
           El cajón era un `space-between` de tres piezas: bastaba un icono de menos a un lado
           para correrlo.

       EL AVISO SE MIDE CADA VEZ QUE SE ABRE, no al cargar: se desplaza con la página (no es
       pegajoso), y si ya estaba fuera de pantalla el máximo con 0 deja el cajón arriba del
       todo, que es lo correcto. Los otros cinco se copian en vez de repetirse a mano: si
       Horizon cambia los suyos, estos van detrás.

   3 · ABRE Y CIERRA. Un solo oyente de `pointerover` en el documento, no uno por ítem: la
       lista del nivel 1 de Horizon la reordena un `overflow-list`, y los oyentes atados a un
       ítem se pierden cuando lo vuelve a pintar. Se empareja por el `href` del enlace, que es
       el mismo dato del que sale el panel en Liquid.

   4 · EL CAJÓN NO SE REINVENTA. Lo abre el <details> nativo del encabezado, así que el icono
       de hamburguesa sigue volviéndose X y el bloqueo de scroll sigue siendo el suyo. Aquí
       solo se observa su atributo `open` y se enseña el nuestro.

   5 · EL SLIDER. Dos tarjetas por clic. La animación es del navegador (`scroll-behavior`),
       y se apaga con `prefers-reduced-motion`.

   6 · «MIS PUNTOS» EN LA BARRA. La pastilla nace en un <template> de nuestra sección y aquí se
       CLONA dentro del encabezado nativo, antes del buscador. El archivo nativo no se toca
       (D-015), y si esto no corre la barra se queda como estaba. Abrir el panel no es cosa
       nuestra: Smile escucha cualquier enlace a `#smile-home`, también uno puesto después de
       cargar (medido el 2026-10-01). SOP-apps-integradas §10.
   --------------------------------------------------------------------------- */

(() => {
  const raiz = document.documentElement;
  const contenedor = document.querySelector('[data-aud-menu]');
  if (!contenedor) return;

  const paneles = Array.from(contenedor.querySelectorAll('[data-aud-panel]'));
  const velo = contenedor.querySelector('[data-aud-velo]');
  const cajon = contenedor.querySelector('[data-aud-cajon]');
  const encabezado = document.getElementById('header-component');
  const suave = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const celular = () => window.matchMedia('(max-width: 749px)').matches;

  if (paneles.length > 0) raiz.classList.add('aud-menu-ok');
  if (cajon) raiz.classList.add('aud-cajon-ok');

  /* --------------------------------------------------------------- 2 · las anclas */
  /* El aviso es una sección del grupo de encabezado y su id lleva un sufijo aleatorio, así que
     se busca por la pieza que sí es estable —`.announcement-bar`— y se sube a su sección. Si
     el cliente lo apaga desde el editor, esto da `null`, la variable se queda en 0 y el cajón
     vuelve a arrancar arriba del todo: el fallo cae del lado seguro. */
  const barraAviso = document.querySelector('.shopify-section-group-header-group .announcement-bar');
  const aviso = barraAviso ? barraAviso.closest('.shopify-section') : null;
  const columnas = encabezado ? encabezado.querySelector('.header__columns') : null;
  const logoEnc = encabezado ? encabezado.querySelector('.header-logo') : null;

  function anclar() {
    if (encabezado) {
      const caja = encabezado.getBoundingClientRect();
      contenedor.style.setProperty('--aud-m-top', Math.max(0, Math.round(caja.bottom)) + 'px');
      contenedor.style.setProperty('--aud-m-barra', Math.round(caja.height) + 'px');
    }
    if (aviso) {
      const abajo = Math.max(0, Math.round(aviso.getBoundingClientRect().bottom));
      contenedor.style.setProperty('--aud-m-aviso', abajo + 'px');
    }
    if (columnas) {
      /* El ancho de columna de iconos es de Horizon: `--header-mobile-bookend`, medido en 44 px.
         Se copia en vez de repetirlo para que no haya dos verdades. El relleno lateral se lee ya
         resuelto: en celular vive en este mismo elemento, en escritorio sale de la rejilla de la
         fila y da 0, que tambien es el valor correcto alli. */
      const c = getComputedStyle(columnas);
      const lado = c.getPropertyValue('--header-mobile-bookend').trim();
      if (lado) contenedor.style.setProperty('--aud-m-lado', lado);
      contenedor.style.setProperty('--aud-m-pad', c.paddingInlineStart);
    }
    if (logoEnc) {
      /* LA IMAGEN, NO SU ENLACE. Medido en vivo: el <a> del logo mide 192x60 porque llena su
         columna, y la foto que hay dentro mide 97x32, centrada. Midiendo el <a> el logo del
         cajon salia al doble de alto. `object-fit: contain` es justo lo que hace que las dos
         cajas no coincidan, asi que hay que preguntarle a la de dentro. */
      const pieza = logoEnc.querySelector('img, svg') || logoEnc;
      const alto = Math.round(pieza.getBoundingClientRect().height);
      /* Un 0 solo puede venir de una imagen que aun no carga. Se deja el valor anterior: el
         cajon se vuelve a medir al abrirse y al cambiar de tamano la ventana. */
      if (alto > 0) contenedor.style.setProperty('--aud-m-logo', alto + 'px');
    }
  }

  anclar();

  /* ---------------------------------------------------------- 3 · abrir y cerrar */
  const porUrl = new Map();
  paneles.forEach((p) => porUrl.set(p.dataset.audUrl, p));

  let abierto = null;
  let cierre = 0;

  function mostrar(panel) {
    if (panel === abierto) return;
    if (abierto) abierto.classList.remove('esta-abierto');
    abierto = panel || null;
    if (abierto) {
      anclar();
      abierto.classList.add('esta-abierto');
      if (velo) velo.classList.add('esta-visible');
    } else if (velo) {
      velo.classList.remove('esta-visible');
    }
  }

  function cerrarPronto() {
    clearTimeout(cierre);
    cierre = window.setTimeout(() => mostrar(null), 140);
  }

  function cancelarCierre() {
    clearTimeout(cierre);
  }

  document.addEventListener('pointerover', (evento) => {
    if (celular() || paneles.length === 0) return;
    const destino = evento.target;
    if (!destino || typeof destino.closest !== 'function') return;

    const item = destino.closest('.menu-list__list-item');
    if (item) {
      const enlace = item.querySelector('a.menu-list__link');
      const panel = enlace ? porUrl.get(enlace.getAttribute('href')) : null;
      cancelarCierre();
      mostrar(panel || null);
      return;
    }

    if (destino.closest('[data-aud-panel]')) {
      cancelarCierre();
      return;
    }

    if (abierto) cerrarPronto();
  });

  document.addEventListener('keydown', (evento) => {
    if (evento.key === 'Escape' && abierto) mostrar(null);
  });

  let pendiente = 0;
  window.addEventListener(
    'scroll',
    () => {
      if (!abierto || pendiente) return;
      pendiente = window.requestAnimationFrame(() => {
        pendiente = 0;
        anclar();
      });
    },
    { passive: true }
  );

  window.addEventListener('resize', anclar);

  /* ------------------------------------------------------------- 4 · el cajón */
  const detalle = document.getElementById('Details-menu-drawer-container');

  if (cajon && detalle) {
    /* `esta`, no `abierto`: ese nombre ya es del panel de escritorio unas líneas más arriba y
       aquí solo lo taparía. */
    const sincronizar = () => {
      const esta = detalle.hasAttribute('open');
      /* Se mide ANTES de enseñarlo, no al cargar la página: entre una cosa y otra el comprador
         ha podido desplazar, y con el aviso ya fuera de pantalla el cajón tiene que arrancar
         en 0. Medirlo una sola vez al arrancar es justo el error que deja el salto. */
      if (esta) anclar();
      cajon.classList.toggle('esta-abierto', esta);
    };

    new MutationObserver(sincronizar).observe(detalle, {
      attributes: true,
      attributeFilter: ['open'],
    });

    sincronizar();

    const cerrar = cajon.querySelector('[data-aud-cerrar]');
    if (cerrar) {
      cerrar.addEventListener('click', () => {
        detalle.removeAttribute('open');
        const resumen = detalle.querySelector('summary');
        if (resumen) resumen.setAttribute('aria-expanded', 'false');
      });
    }
  }

  /* ------------------------------------------------------------ 5 · el slider */
  contenedor.addEventListener('click', (evento) => {
    const boton = evento.target.closest('[data-aud-desliza]');
    if (!boton) return;

    const caja = contenedor.querySelector('[data-aud-slider="' + boton.dataset.audPara + '"]');
    if (!caja) return;

    /* El hueco se LEE, no se supone: el slider de escritorio va a 16 px y el del cajon a 12,
       y con el 20 fijo de antes el paso se pasaba de largo y dejaba media tarjeta cortada. */
    const primera = caja.firstElementChild;
    const hueco = parseFloat(getComputedStyle(caja).columnGap) || 16;
    const ancho = primera ? primera.getBoundingClientRect().width + hueco : 200;
    const paso = ancho * 2;

    caja.scrollBy({
      left: boton.dataset.audDesliza === 'atras' ? -paso : paso,
      behavior: suave ? 'smooth' : 'auto',
    });
  });

  /* ------------------------------------------------ 6 · «Mis puntos» en la barra */
  /* Se busca todo de nuevo en cada llamada, sin fiarse de las referencias de arriba: en el
     editor de temas el encabezado o nuestra sección se pueden volver a pintar, y entonces la
     copia vieja se fue con ellos o el molde trae otro texto. Quitar y volver a clonar deja
     siempre una sola pastilla, la vigente. */
  function ponerPastilla() {
    const barra = document.getElementById('header-component');
    if (!barra) return;
    barra.querySelectorAll('.aud-mpts').forEach((vieja) => vieja.remove());

    const molde = document.querySelector('[data-aud-menu] template[data-aud-pastilla]');
    const derecha = barra.querySelector('.header__column--right');
    const pastilla = molde ? molde.content.firstElementChild : null;
    if (!pastilla || !derecha) return;
    derecha.prepend(pastilla.cloneNode(true));
  }

  ponerPastilla();
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', ponerPastilla);
  }
})();
